import { useCallback, useState } from "react";

import { useApi } from "../useApi";

export interface CreateShareArgs {
  assetIds: string[];
  /** Allow the recipient to download the original (hi-res) file. */
  allowDownload: boolean;
  /** Allow the recipient to download the proxy file. */
  allowDownloadProxy: boolean;
  title?: string;
  /** ISO-8601 expiry timestamp. When omitted, the backend defaults to 1 year. */
  expires?: string;
}

interface CreateShareResponse {
  url?: string;
  expires?: string;
}

/**
 * Creates a public, time-limited Iconik share link via `POST /share`.
 *
 * One asset id produces a per-asset share; multiple ids produce a single
 * bundled share (the backend picks the right Iconik endpoint). Goes through
 * `useApi` so the request carries the Cognito auth header, same as every
 * other call in the app.
 */
const useShareClip = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { apiPost } = useApi();

  const createShare = useCallback(
    async ({
      assetIds,
      allowDownload,
      allowDownloadProxy,
      title,
      expires,
    }: CreateShareArgs): Promise<string | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiPost<CreateShareResponse>("/share", {
          assetIds,
          allowDownload,
          allowDownloadProxy,
          ...(title ? { title } : {}),
          ...(expires ? { expires } : {}),
        });
        if (!response?.url) {
          throw new Error("no share url returned");
        }
        return response.url;
      } catch (e) {
        console.error(e);
        setError("Failed to create share link");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [apiPost]
  );

  return { createShare, loading, error };
};

export default useShareClip;
